import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, ArrowDown } from "lucide-react";
import { ChapterRail } from "@/components/WorkNav";

const PROCESS = [
  { title: "Design", sub: "Format the idea", color: "#2563eb" },
  { title: "Operate", sub: "Make it work", color: "#f97316" },
  { title: "Activate", sub: "Bring people in", color: "#16a34a" },
  { title: "Scale", sub: "Make it travel", color: "#9333ea" },
];

const BUILD = [
  { num: "01", title: "Competitive Properties", sub: "Formats that travel across cities + seasons" },
  { num: "02", title: "Event Management", sub: "Sport, logistics and event-day delivery" },
  { num: "03", title: "Advisory + Consulting", sub: "Strategy, formats and property thinking" },
];

const CHAPTERS = [
  { num: "01", name: "Red Bull", note: "Global properties. Indian competition journeys.", to: "/work/red-bull" },
  { num: "02", name: "Kotak Mahindra Bank", note: "A single tournament that became a sporting relationship.", to: "/work/kotak" },
  { num: "03", name: "IDFC First Bank", note: "From an event to an internal sporting programme.", to: "/work/idfc" },
  { num: "04", name: "AT20", note: "Building and sustaining a sporting property.", to: "/work/at20" },
];

const Header = () => (
  <header className="fixed top-0 inset-x-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/10">
    <div className="mx-auto max-w-[1500px] w-full px-5 sm:px-8 h-16 flex items-center justify-between">
      <Link to="/" data-testid="site-logo" className="shrink-0">
        <img src="/owos-logo-badge.png" alt="One World of Sports" className="h-11 w-auto object-contain" />
      </Link>
      <Link
        to="/#gateways"
        data-testid="work-back-explore-top"
        className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-neutral-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
        Explore
      </Link>
    </div>
  </header>
);

const Reveal = ({ children, className = "", delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1], delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Work() {
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    document.title = "Work — One World of Sports";
  }, []);

  return (
    <div data-testid="work-page" className="bg-[#0a0a0a] min-h-screen text-white">
      <Header />
      <ChapterRail currentKey="intro" />
      <motion.div
        style={{ scaleX: scrollYProgress }}
        className="fixed top-16 left-0 right-0 z-50 h-[2px] origin-left bg-[#e62429]"
      />

      {/* SLIDE 1 — WORK opener */}
      <section
        data-testid="work-hero"
        className="relative min-h-[92svh] flex items-center mx-auto max-w-[1500px] px-5 sm:px-8 pt-28"
      >
        <div className="absolute left-0 top-1/2 -translate-y-1/2 h-40 w-1.5 bg-[#e62429] hidden sm:block" />
        <Reveal className="sm:pl-10">
          <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-[#e62429]">
            <span className="h-px w-10 bg-[#e62429]" />
            Oneworld of Sports · Work
          </p>
          <h1 className="mt-6 font-display uppercase text-white text-[26vw] sm:text-[20vw] lg:text-[16rem] leading-[0.8]">
            Work
          </h1>
          <p className="mt-8 font-display uppercase text-[#e62429] text-3xl sm:text-5xl leading-none">
            Sport. Built to move.
          </p>
          <p className="mt-6 max-w-xl text-lg sm:text-xl leading-relaxed text-neutral-300">
            A portfolio of sporting systems designed, operated, activated and scaled in the real world.
          </p>
        </Reveal>
      </section>

      {/* SLIDE 2 — Built for the corporate player */}
      <section
        data-testid="work-founding"
        className="border-t border-white/10 mx-auto max-w-[1500px] px-5 sm:px-8 py-24 sm:py-36"
      >
        <Reveal>
          <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.9] max-w-4xl">
            Built for the corporate player.
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <Reveal className="lg:col-span-3">
            <p className="font-display text-6xl sm:text-7xl text-[#e62429] leading-none">2007</p>
            <p className="mt-3 text-xs uppercase tracking-[0.3em] font-bold text-neutral-500">Founded</p>
          </Reveal>
          <Reveal className="lg:col-span-9" delay={0.05}>
            <p className="max-w-2xl text-xl sm:text-2xl leading-relaxed text-neutral-200">
              Oneworld of Sports was founded to create competitive sport for non-professionals inside the corporate environment.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 border-t border-white/15" />

        <div className="mt-12 max-w-3xl ml-auto space-y-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] font-bold text-neutral-500">From</p>
            <p className="mt-3 font-display uppercase text-white text-3xl sm:text-5xl leading-none">
              “Let’s organise a tournament.”
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="text-xs uppercase tracking-[0.3em] font-bold text-neutral-500">To</p>
            <p className="mt-3 font-display uppercase text-[#e62429] text-3xl sm:text-5xl leading-none">
              “Let’s build a sports property.”
            </p>
          </Reveal>
        </div>
      </section>

      {/* SLIDE 3 — What we actually build */}
      <section
        data-testid="work-build"
        className="border-t border-white/10 bg-[#0f0f0f] py-24 sm:py-36"
      >
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.9] max-w-4xl">
              What we actually build.
            </h2>
          </Reveal>

          <div className="mt-16 flex flex-col lg:flex-row lg:items-stretch gap-8 lg:gap-4">
            {BUILD.map((b, i) => (
              <Reveal key={b.num} className="flex-1" delay={i * 0.08}>
                <div className="relative">
                  <span className="font-display text-2xl text-[#e62429]">{b.num}</span>
                  <h3 className="mt-3 font-display uppercase text-white text-3xl sm:text-4xl leading-[0.95]">
                    {b.title}
                  </h3>
                  <p className="mt-4 text-base leading-relaxed text-neutral-400 max-w-xs">
                    {b.sub}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-20 sm:mt-28">
            <p className="font-display uppercase text-white text-2xl sm:text-4xl leading-tight">
              The output is not just an event.
            </p>
            <p className="mt-2 font-display uppercase text-[#e62429] text-3xl sm:text-6xl leading-[0.95]">
              It is a repeatable sporting system.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SLIDE 4 — Design. Operate. Activate. Scale. (continuous with ABOUT) */}
      <section
        data-testid="work-process"
        className="border-t border-white/10 py-24 sm:py-36"
      >
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.9]">
              Design. Operate. Activate. Scale.
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-0 border-t border-l border-white/12">
            {PROCESS.map((step, i) => (
              <motion.div
                key={step.title}
                data-testid={`work-process-step-${step.title.toLowerCase()}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.65, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="group relative border-r border-b border-white/12 p-8 sm:p-10 min-h-[240px] flex flex-col justify-between overflow-hidden"
              >
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ backgroundColor: step.color }}
                />
                <div className="relative z-10 flex items-center justify-between">
                  <span className="font-display text-xl text-neutral-500 group-hover:text-white/80 transition-colors">
                    0{i + 1}
                  </span>
                  {i < PROCESS.length - 1 && (
                    <ArrowRight className="h-6 w-6 text-neutral-600 group-hover:text-white transition-colors" />
                  )}
                </div>
                <div className="relative z-10">
                  <h3
                    className="font-display uppercase text-4xl sm:text-5xl transition-colors duration-300"
                    style={{ color: step.color }}
                  >
                    <span className="group-hover:text-white transition-colors duration-300">
                      {step.title}
                    </span>
                  </h3>
                  <p className="mt-2 text-sm uppercase tracking-[0.15em] font-bold text-neutral-400 group-hover:text-white/90 transition-colors">
                    {step.sub}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <Reveal className="mt-10">
            <p className="font-display uppercase text-neutral-300 text-xl sm:text-2xl">
              One operating logic. Different sports, audiences and scales.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SLIDE 5 — Four stories. Four different briefs. (threshold) */}
      <section
        data-testid="work-threshold"
        className="border-t border-white/10 bg-[#0c0c0c] py-24 sm:py-40"
      >
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display uppercase text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.9] max-w-4xl">
              Four stories. Four different briefs.
            </h2>
            <p className="mt-5 font-display uppercase text-[#e62429] text-xl sm:text-3xl">
              One operating instinct.
            </p>
          </Reveal>

          <div className="mt-14 border-t border-white/12">
            {CHAPTERS.map((c, i) =>
              c.to ? (
                <Reveal key={c.num} delay={i * 0.06}>
                  <Link
                    to={c.to}
                    data-testid={`threshold-item-${i}`}
                    className="group grid grid-cols-1 sm:grid-cols-12 items-baseline gap-2 sm:gap-6 border-b border-white/12 py-7 sm:py-9 transition-colors hover:bg-white/[0.03]"
                  >
                    <span className="sm:col-span-1 font-display text-xl text-[#e62429]">{c.num}</span>
                    <h3 className="sm:col-span-5 font-display uppercase text-white text-3xl sm:text-4xl leading-none tracking-tight group-hover:translate-x-1 transition-transform">
                      {c.name}
                    </h3>
                    <p className="sm:col-span-5 text-base sm:text-lg leading-relaxed text-neutral-400">
                      {c.note}
                    </p>
                    <span className="sm:col-span-1 flex sm:justify-end">
                      <ArrowUpRight className="h-6 w-6 text-neutral-500 group-hover:text-[#e62429] transition-colors" />
                    </span>
                  </Link>
                </Reveal>
              ) : (
                <Reveal key={c.num} delay={i * 0.06}>
                  <div
                    data-testid={`threshold-item-${i}`}
                    className="grid grid-cols-1 sm:grid-cols-12 items-baseline gap-2 sm:gap-6 border-b border-white/12 py-7 sm:py-9"
                  >
                    <span className="sm:col-span-1 font-display text-xl text-neutral-600">{c.num}</span>
                    <h3 className="sm:col-span-5 font-display uppercase text-white text-3xl sm:text-4xl leading-none tracking-tight">
                      {c.name}
                    </h3>
                    <p className="sm:col-span-6 text-base sm:text-lg leading-relaxed text-neutral-400">
                      {c.note}
                    </p>
                  </div>
                </Reveal>
              )
            )}
          </div>

          <Reveal className="mt-14 max-w-2xl">
            <p className="text-lg sm:text-xl leading-relaxed text-neutral-300">
              Four examples of how a brief changes when it meets the real world.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CONCLUSION — Slides 64-65 */}
      <section data-testid="work-conclusion" id="work-conclusion" className="border-t border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 py-28 sm:py-40 text-center">
          <Reveal>
            <p className="font-display uppercase text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
              One operating instinct.
            </p>
          </Reveal>
        </div>

        <div className="border-t border-white/10 py-24 sm:py-36">
          <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
            <Reveal>
              <h2 className="font-display uppercase text-white text-3xl sm:text-5xl leading-[0.95]">
                Different briefs. Different sports. Different audiences.
              </h2>
            </Reveal>
            <div className="mt-12 border-t border-white/12">
              {[
                ["Red Bull", "Competition properties"],
                ["Kotak", "Corporate engagement"],
                ["IDFC First Bank", "Internal sporting programme"],
                ["AT20", "Owned sporting property"],
              ].map(([name, kind], i) => (
                <Reveal key={name} delay={i * 0.06}>
                  <div className="grid grid-cols-1 sm:grid-cols-12 items-baseline gap-1 sm:gap-6 border-b border-white/12 py-5 sm:py-6">
                    <span className="sm:col-span-5 font-display uppercase text-white text-2xl sm:text-3xl leading-none">{name}</span>
                    <span className="sm:col-span-7 text-base sm:text-lg text-neutral-400">{kind}</span>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal className="mt-16">
              <p className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.95]">The work changes.</p>
              <p className="mt-1 font-display uppercase text-[#e62429] text-4xl sm:text-6xl leading-[0.95]">The operating instinct doesn't.</p>
            </Reveal>
          </div>
        </div>

        {/* Return to where WORK began */}
        <div className="border-t border-white/10 py-28 sm:py-44">
          <div className="mx-auto max-w-[1500px] px-5 sm:px-8 text-center">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.4em] text-neutral-500 font-bold">Oneworld of Sports</p>
              <p className="mt-8 font-display uppercase text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.85]">
                Sport.<br />Built to move.
              </p>
              <p className="mt-10 text-sm sm:text-base uppercase tracking-[0.3em] text-neutral-400 font-bold">
                Design. Operate. Activate. Scale.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Route back to Explore */}
      <section className="border-t border-white/10 py-16">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 flex justify-center">
          <Link
            to="/#gateways"
            data-testid="work-back-explore-bottom"
            className="group inline-flex items-center gap-3 border border-white/20 px-8 py-4 text-xs uppercase tracking-[0.28em] font-bold text-white hover:bg-white hover:text-black transition-colors"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
            Back to Explore
            <ArrowUpRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
          </Link>
        </div>
      </section>
    </div>
  );
}
